import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Form from "../../../Form/Form";
import Input from "../../../Form/Input/Input";
import DefaultButton from "../../../../components/simple/DefaultButton";
import DragDropFileUpload from "../../../DragDropFileUpload/DragDropFileUpload";

import { Avatar } from "@mui/material";
import { PersonOutline } from "@mui/icons-material";


const Personal = ({
  user,
  setLoading,
  setError,
  setSuccess,
  brand,
  authToken,
  partialAuthToken,
  fetchUser,
}) => {
  const navigate = useNavigate();
  const [previewImage, setPreviewImage] = useState(user?.profile_picture);
  const [mobNumber, setMobNumber] = useState(user?.mobile_number || "");
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const is2FAReferrer = searchParams.get("refferer") === "2fa";
  const fields = [
    {
      id: "firstName",
      label: "Name",
      fields: [
        {
          id: "first_name",
          name: "first_name",
          type: "text",
          placeholder: "First Name",
        },
        {
          id: "last_name",
          name: "last_name",
          type: "text",
          placeholder: "Last Name",
        },
      ],
    },
    {
      id: "email",
      label: "Email Address",
      fields: [
        {
          id: "email",
          name: "email",
          type: "email",
          placeholder: "Email Address",
        },
      ],
    },
    {
      id: "job_role",
      label: "Job Role",
      fields: [
        {
          id: "job_role",
          name: "job_role",
          type: "text",
          placeholder: "Job Role",
        },
      ],
    },
    {
      id: "mobile_number",
      label: "Phone Number",
      fields: [
        {
          id: "mobile_number",
          name: "mobile_number",
          type: "mobile",
          placeholder: "Mobile number",
          // helpText: "You will need to verify your device after next login if you change this",
        },
      ],
    },
  ];

  const handleError = (err) => {
    setSuccess(false);
    setError(err);
    setLoading(false);
  };

  const handleFilesSelected = (
    files,
    setUploadProgress,
    setError,
    setSuccess
  ) => {
    files.forEach((file, index) => {
      const formData = new FormData();
      // 1. Label changed from "image" to "profile_picture"
      formData.append("profile_picture", file);

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress((prev) =>
            prev.map((item, i) => (i === index ? { ...item, progress } : item))
          );
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.response);
          console.log(`Upload success for ${file.name}:`, xhr.response);
          setSuccess(true);
          setPreviewImage(response.profile_picture);
          fetchUser();
        } else {
          setError(`Upload failed for ${file.name}: ${xhr.statusText}`);
        }
      };

      xhr.onerror = () => {
        setError(`Upload failed for ${file.name}: Network error`);
      };

      // 2. Updated to PATCH method and dynamic /user/{id} URL
      xhr.open("PATCH", `${brand.api_base_url}/user/${user.id}/`, true);

      // 3. Added Authorization header with the token
      xhr.setRequestHeader("Authorization", `Token ${authToken}`);

      xhr.send(formData);
    });
  };

  return (
    <div className="profile-fields-container">
      <Form
        action={`user/${user.id}/`}
        method="PATCH"
        onError={handleError}
        onBefore={() => setLoading(true)}
        onSuccess={(data) => {
          if (data['reverify_2fa']){
            navigate("/2fa_verify/")
          }
          if (data["mobile_number_masked"]){
            localStorage.setItem("2fa_mobile_number_masked", data['mobile_number_masked'])
          }
          setLoading(false);
          setSuccess(true);
          fetchUser();
          return;
        }}
        auth
      >
        {({ register, formState: { errors }, control, setValue, watch }) => {
          // Fix for mobile number validation bug
          // react-phone-input
          const watchedMobile = watch("mobile_number");
          useEffect(() => {
            if (watchedMobile) {
              setMobNumber(watchedMobile);
            }
          }, [watchedMobile]);
          useEffect(() => {
            if (user?.mobile_number) {
              setValue("mobile_number", user.mobile_number, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
          }, [user?.mobile_number, setValue]);
          return (
            <>
              {/* <input type="hidden" name="id" {...register("id")} value={user.id} /> */}
              {fields.map((section, sectionIndex) => (
                <div key={sectionIndex} className="form-section">
                  <label htmlFor={section.id}>{section.label}</label>
                  <div className="field-group">
                    {section.fields.map((field, fieldIndex) => (
                      <Input
                        id={field.name}
                        label={field.label}
                        showLabel={false}
                        key={fieldIndex}
                        type={field.type || "text"}
                        placeHolder={field.placeholder}
                        register={register(field.name, {
                          required: "Field is required",
                        })}
                        error={errors[field.name]?.message}
                        control={control}
                        value={user[field.id]}
                        helpText={field.helpText}
                      />
                    ))}
                  </div>
                </div>
              ))}
              <div className="form-section">
                <label>
                  Your Photo{" "}
                  <span>This will be displayed on your profile.</span>
                </label>
                <div className="field-group">
                  <div className="avatar-container">
                    {user && user.profile_picture ? (
                      <img
                        src={previewImage}
                        alt="Profile"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          display: "flex",
                          position: "relative",
                          mx: "auto",
                          alignItems: "center",
                          backgroundColor: "#01ECFF",
                          color: "#02002B",
                          cursor: "pointer",
                          width: 50,
                          height: 50,
                          margin: 0,
                        }}
                      >
                        <PersonOutline />
                      </Avatar>
                    )}

                    <DragDropFileUpload
                      onFilesSelected={handleFilesSelected}
                      maxFiles={1}
                      accept="image/jpeg,image/png,image/gif,image/svg+xml"
                      maxSize={2 * 1024 * 1024}
                    />
                  </div>
                </div>
              </div>
              <div className="form-actions">
                <DefaultButton buttonSize="md" wideOnMobile type="submit">
                  Save Changes
                </DefaultButton>
              </div>
            </>
          );
        }}
      </Form>
    </div>
  );
};

export default Personal;
